// ─── Street View thumbnails ───────────────────────────────────────────────
// Static Street View images for the map's result cards. Sourcing properties
// and comps have no photo column, so this is the only imagery available for
// the bulk of the pins.
//
// WHY THIS IS NOT JUST A URL ANY MORE
//
// It used to build a static URL straight from a lat/lng with source=outdoor,
// on the assumption that outdoor meant "shot by the Street View car". It does
// not. `outdoor` only excludes indoor COLLECTIONS; photospheres uploaded by
// 360-tour companies come back exactly the same way, and those firms shoot
// building interiors for a living. An audit of 450 captured deal frames found
// 47 of them - Threshold 360, Instant360, and a dozen photographers by name -
// which is why cards were showing the inside of a tenant's unit.
//
// The Static api gives no way to ask for Google's own imagery: source=google
// is rejected with a 400. The JAVASCRIPT api does, via
// StreetViewSource.GOOGLE, so the panorama is chosen through StreetViewService
// first and the image is then fetched BY PANORAMA ID. Fetching by id matters:
// asking the static endpoint by location would just re-run Google's own search
// and handed back the same photosphere that was rejected.
//
// It also stops paying for misses. Every card used to bill an image request
// even where no imagery existed, and only found out from the 404. The lookup
// now happens first, and a location with nothing at it costs nothing.

const KEY = () =>
  process.env.REACT_APP_GOOGLE_GEOCODE_KEY || process.env.REACT_APP_GOOGLE_MAPS_KEY;

// How far Google may wander from the pin to find a panorama.
const RADIUS_M = 80;

// Below this, a heading aimed at the building is a picture of its wall - the
// pin is a rooftop coordinate, so a panorama standing in the property's own
// yard looks straight at the side of it. Above it the building is far enough
// away to be in frame. Matches PropertyMedia, which made the same call.
const FACE_BUILDING_FROM_M = 35;

// Rounded to ~11m so two pins on the same building share one entry.
const cacheKey = (lat, lng) => `${lat.toFixed(4)},${lng.toFixed(4)}`;
const cache = new Map();          // key -> Promise<string|null>

function source(g) {
  // Guarded: GOOGLE is a relatively recent addition, and passing undefined
  // would widen the search to include indoor panoramas - worse than OUTDOOR.
  return g.StreetViewSource.GOOGLE || g.StreetViewSource.OUTDOOR;
}

function findPanorama(g, lat, lng) {
  return new Promise((resolve) => {
    new g.StreetViewService().getPanorama(
      { location: { lat, lng }, radius: RADIUS_M, source: source(g) },
      (data, status) => {
        if (status !== "OK" || !data?.location?.latLng) { resolve(null); return; }
        const sph = g.geometry && g.geometry.spherical;
        const target = new g.LatLng(lat, lng);
        const away = sph ? sph.computeDistanceBetween(data.location.latLng, target) : 0;
        const heading = (sph && away >= FACE_BUILDING_FROM_M)
          ? sph.computeHeading(data.location.latLng, target)
          : ((data.tiles && data.tiles.centerHeading) || 0);
        resolve({ pano: data.location.pano, heading });
      },
    );
  });
}

/**
 * Resolve a thumbnail URL for a pin, or null when Google has no imagery there.
 * Returns a Promise — the panorama has to be looked up before the image can be
 * asked for by id.
 */
export function streetViewThumbUrl(lat, lng, { width = 400, height = 220, fov = 80 } = {}) {
  const key = KEY();
  if (!key || !Number.isFinite(lat) || !Number.isFinite(lng)) return Promise.resolve(null);

  const ck = `${cacheKey(lat, lng)}|${width}x${height}`;
  if (cache.has(ck)) return cache.get(ck);

  const g = window.google && window.google.maps;
  if (!g) return Promise.resolve(null);   // Maps not loaded yet; the card retries

  const p = findPanorama(g, lat, lng).then((hit) => {
    if (!hit) return null;
    return "https://maps.googleapis.com/maps/api/streetview" +
      `?size=${width}x${height}` +
      `&pano=${encodeURIComponent(hit.pano)}` +
      `&heading=${hit.heading.toFixed(1)}` +
      `&fov=${fov}` +
      "&pitch=6" +
      "&return_error_code=true" +
      `&key=${key}`;
  });
  cache.set(ck, p);
  return p;
}

export function hasStreetViewKey() {
  return !!KEY();
}
